import { ApolloCache } from '@apollo/client';

import { IRoleScope } from './member-list.types';

interface IScopedRolesEntry {
  __typename?: string;
  ageGroupId?: string | null;
  assessmentId?: string | null;
  roles: string[];
}

// Rewrites only this scope's roles on the cached member, so saves on different scopes compose in any order.
// Returns what it replaced for a rollback; null means no entry, and writing null removes the entry.
export const writeScopedRoles = (cache: ApolloCache<unknown>, { memberId, ageGroupId, assessmentId, roles }: IRoleScope & { roles: string[] | null }) => {
  let replaced: string[] | null = null;
  const id = cache.identify({ __typename: 'Member', _id: memberId });
  if (!id) {
    console.warn(`writeScopedRoles: no cache id for member ${memberId}; roles not written`);
    return replaced;
  }

  const replaceScopeEntry = (entries: readonly IScopedRolesEntry[] | null, idField: 'ageGroupId' | 'assessmentId', scopeId: string) => {
    const current = entries || [];
    const existing = current.find(entry => entry[idField] === scopeId);
    if (existing) replaced = existing.roles;
    if (!roles) return current.filter(entry => entry !== existing);
    if (!existing) return [...current, { __typename: 'MemberRoles', [idField]: scopeId, roles }];

    return current.map(entry => entry === existing ? { ...entry, roles } : entry);
  };

  cache.modify({
    id,
    fields: {
      ...(ageGroupId ? { rolesByAgeGroup: (entries: IScopedRolesEntry[] | null) => replaceScopeEntry(entries, 'ageGroupId', ageGroupId) } : {}),
      ...(assessmentId ? { rolesByAssessment: (entries: IScopedRolesEntry[] | null) => replaceScopeEntry(entries, 'assessmentId', assessmentId) } : {}),
      ...(!ageGroupId && !assessmentId ? { superUserRoles: (current: string[] | null) => { replaced = current; return roles; } } : {}),
    },
  });

  return replaced;
};
